import { useSettings } from 'app/hooks/useSettings'
import type { ListView } from 'app/types/navigation'
import { bg } from 'lib/background'

const LAST_VIEW_KEY = `listView`

/**
 * The view a list opens in: the last view picked on any list, so moving between lists keeps
 * the table (or board) you were in. A list that does not offer that view falls back to the
 * one last picked on it. Same shape as `useSettings`: the view, a save, and whether it loaded.
 */
export function useRememberedListView(
  listId: string,
  views: readonly ListView[],
): [ListView | undefined, (view: ListView) => void, boolean] {
  const listKey = `listView-${listId}`
  const [lastView, saveLastView, lastLoaded] = useSettings<ListView>(LAST_VIEW_KEY)
  const [listView, saveListView, listLoaded] = useSettings<ListView>(listKey)
  // Once the shared view answers, the per-list read never holds the page blank, so switching
  // lists draws the next one straight away instead of waiting on its own setting.
  const lastOffered = lastLoaded && views.includes(lastView as ListView)

  const save = (view: ListView): void => {
    bg(() => saveLastView(LAST_VIEW_KEY, view))
    bg(() => saveListView(listKey, view))
  }
  return [lastOffered ? lastView : listView, save, lastOffered || (lastLoaded && listLoaded)]
}
